import React, { useState } from 'react'
import { useMediaCore } from './hooks/useMediaCore'
import { MediaHeader } from './components/MediaHeader'
import { MediaContent } from './components/MediaContent'
import { MediaSidebar } from './components/MediaSidebar'
import { UploadDialog } from './components/UploadDialog'
import { NewFolderDialog } from './components/NewFolderDialog'
import { ConfirmDialog } from './components/ConfirmDialog'
import { Dialog, DialogContent } from '@iws/admin-ui/dialog'
import { Button } from '@iws/admin-ui/button'
import { MediaFile, MediaServiceConfig, PickedFile } from './types'

interface MediaPickerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  service: MediaServiceConfig
  resolveUrl: (path: string) => string
  onSelect: (files: PickedFile[]) => void
  multiple?: boolean
}

export const MediaPickerModal = ({
  open,
  onOpenChange,
  service,
  resolveUrl,
  onSelect,
  multiple = false,
}: MediaPickerModalProps) => {
  const core = useMediaCore({ service, open, multiple })
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const confirmFiles = (files: MediaFile[]) => {
    onSelect(
      files.map((f) => ({
        _id: f._id,
        name: f.name,
        path: f.path,
        url: resolveUrl(f.path),
        type: f.type,
        size: f.size,
      }))
    )
    onOpenChange(false)
  }

  const handleConfirm = () => confirmFiles(core.selectedFiles)

  const handleDeleteClick = (type: 'file' | 'folder', id: string, name: string) => {
    core.setDeleteTarget({ type, id, name })
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!core.deleteTarget) return
    if (core.deleteTarget.type === 'file') {
      await core.handleDeleteFile(core.deleteTarget.id)
    } else {
      await core.handleDeleteFolder(core.deleteTarget.id)
    }
    core.setDeleteTarget(null)
  }

  const hasSelection = core.selectedFiles.length > 0
  const selectedFile = core.selectedFiles.length === 1 ? core.selectedFiles[0] : null

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          hideClose
          size="xl"
          className="flex h-[100dvh] max-h-none w-full flex-col gap-0 overflow-hidden rounded-none p-0 sm:h-[85vh] sm:max-h-[85vh] sm:w-[calc(100%-64px)] sm:rounded-xl"
        >
          <MediaHeader
            mode="modal"
            currentFolderId={core.currentFolderId}
            currentFolderName={core.currentFolderName}
            folderCount={core.filteredFolders.length}
            fileCount={core.filteredFiles.length}
            searchTerm={core.searchTerm}
            canUpload={core.canUpload}
            canCreateFolder={core.canCreateFolder}
            onBack={() => core.navigateToFolder(null)}
            onClose={() => onOpenChange(false)}
            onUpload={() => core.setIsUploadOpen(true)}
            onNewFolder={() => core.setIsNewFolderOpen(true)}
            onSearchChange={core.setSearchTerm}
          />

          <div className="relative flex min-h-0 flex-1 overflow-hidden">
            <MediaContent
              isLoading={core.isLoading}
              folders={core.filteredFolders}
              files={core.filteredFiles}
              searchTerm={core.searchTerm}
              selectedFiles={core.selectedFiles}
              multiple={multiple}
              resolveUrl={resolveUrl}
              formatSize={core.formatSize}
              canDelete={core.canDelete}
              navigateToFolder={core.navigateToFolder}
              toggleSelect={core.toggleSelect}
              onActivateFile={multiple ? undefined : (file) => confirmFiles([file])}
              onDeleteFolder={(id, name) => handleDeleteClick('folder', id, name)}
              onDeleteFile={(id, name) => handleDeleteClick('file', id, name)}
            />

            {selectedFile && (
              <MediaSidebar
                selectedFile={selectedFile}
                resolveUrl={resolveUrl}
                formatSize={core.formatSize}
                onDelete={() => handleDeleteClick('file', selectedFile._id, selectedFile.name)}
              />
            )}
          </div>

          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border-subtle bg-card px-4 py-3">
            <div className="flex min-w-0 items-center gap-2 text-sm">
              {hasSelection ? (
                <>
                  <span className="truncate text-ink">
                    {selectedFile ? selectedFile.name : `${core.selectedFiles.length} files selected`}
                  </span>
                  <Button variant="ghost" size="sm" onClick={core.clearSelection}>
                    Clear
                  </Button>
                </>
              ) : (
                <span className="hidden text-text-2 sm:inline">
                  {multiple ? 'Select one or more files' : 'Select a file · double-click to insert'}
                </span>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} disabled={!hasSelection}>
                {multiple && core.selectedFiles.length > 1 ? `Insert ${core.selectedFiles.length} files` : 'Insert'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={`Delete ${core.deleteTarget?.type === 'file' ? 'file' : 'folder'}?`}
        description={`Are you sure you want to delete "${core.deleteTarget?.name}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
      />

      <NewFolderDialog
        open={core.isNewFolderOpen}
        onOpenChange={core.setIsNewFolderOpen}
        onCreate={core.handleCreateFolder}
      />

      <UploadDialog
        open={core.isUploadOpen}
        onOpenChange={core.setIsUploadOpen}
        onUpload={core.handleUpload}
        disabled={!core.currentFolderId}
      />
    </>
  )
}
